import { keys, includes, forOwn, forEach } from 'lodash';
import { ObjectSchema, mixed, string, number } from 'yup';
import 'yup-phone-lite';
import i18n from '@components/i18n';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValidationSchema = ObjectSchema<any>;
type yupMultipleFilesOptions = {
  required?: boolean;
  max?: number;
};

const MAX_FILE_SIZE = 5_000_000; // 5MB
const MAX_FILE_SIZE_STRING = '5MB';
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'];
const DEFAULT_MAX_FILES = 10;

export const yupFile = (required = false) => {
  let schema = mixed<File>();

  if (required) {
    schema = schema.test(
      'required',
      i18n.t('validation:required'),
      (file) => file instanceof File
    );
  }

  return schema
    .test(
      'fileSize',
      i18n.t('validation:fileSizeOutOfRange', { max: MAX_FILE_SIZE_STRING }),
      (file) => !file || file.size < MAX_FILE_SIZE
    )
    .test(
      'fileFormat',
      i18n.t('validation:invalidFormat'),
      (file) => !file || SUPPORTED_FORMATS.includes(file.type)
    );
};

export const yupMultipleFiles = ({ required = false, max = DEFAULT_MAX_FILES }: yupMultipleFilesOptions = {}) => {
  let schema = mixed<File[]>().default([]);

  if (required) {
    schema = schema.test('required', i18n.t('validation:required'), (files: File[]) => files && files.length > 0);
  }

  if (max) {
    schema = schema.test('max', i18n.t('validation:limitOutOfRange', { max }), (files: File[]) => files.length <= max);
  }

  return schema
    .test('fileFormat', i18n.t('validation:invalidFormat'), (files: File[]) => {
      if (!files || files.length === 0) return true;

      let isValid = false;
      forEach(files, (file) => {
        if (!SUPPORTED_FORMATS.includes(file.type)) {
          isValid = false;
          return;
        }

        isValid = true;
      });

      return isValid;
    })
    .test('fileSize', i18n.t('validation:fileSizeOutOfRange', { max: MAX_FILE_SIZE_STRING }), (files: File[]) => {
      if (!files || files.length === 0) return true;

      let isValid = false;
      forEach(files, (file) => {
        if (file.size > MAX_FILE_SIZE) {
          isValid = false;
          return;
        }

        isValid = true;
      });

      return isValid;
    });
};

export const yupCsvFile = () => {
  return mixed()
    .test('fileSize', i18n.t('validation:fileSizeOutOfRange', { max: MAX_FILE_SIZE_STRING }), (file: File) => !file || (file && file.size < MAX_FILE_SIZE))
    .test(
      'fileFormat',
      i18n.t('validation:invalidFormat'),
      (file: File) => !file || (file && includes(['text/csv'], file.type)),
    );
};

export const yupMobileNumber = () => {
  return string()
    .phone('KH', i18n.t('validation:validPhone', { countryName: i18n.t('common:cambodia') }));
}

export const yupOtpCode = () => {
  return string()
    .required(i18n.t('validation:required'))
    .length(4, i18n.t('validation:length'))
    .matches(/\d/, i18n.t('validation:notANumber'));
};

export const yupPercentage = (required = false) => {
  let schema = number();

  if (required) {
    schema = schema.required(i18n.t('validation:required'));
  }
  else {
    schema = schema.optional();
  }

  return schema.min(0).max(100);
}

/* eslint-disable */
export const getFields = (schema: ValidationSchema) => {
  return schema?.describe()?.fields;
};

export const getFieldKeys = (schema: ValidationSchema) => {
  return keys(getFields(schema));
};
/* eslint-enable */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isRequired = <T extends object>(schema: ValidationSchema) => {
  const result = {} as T;
  forOwn(schema.fields, (field, key) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    result[key as keyof T] = !!field?.exclusiveTests?.required as T[keyof T];
  });
  return result;
};
